import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  Chip,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Typography,
  Popover,
  Collapse,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import LanguageIcon from '@material-ui/icons/Language';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import FacebookIcon from '@material-ui/icons/Facebook';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import SearchIcon from '@material-ui/icons/Search';
import { CustomTooltip } from 'components/CustomTooltip/CustomTooltip';
import { AppTabs, EmptyView, RelatedCompanyChip } from 'components';
import { useDebounce, useDocumentTitle } from 'hooks';
import Flag from '@material-ui/icons/Flag';
import { fetchClient, fetchClients, getPlatformIds } from 'lib/Api/Clients';
import { stringAvatar } from 'lib/Helper/Avatars';
import CloudIcon from '@material-ui/icons/Cloud';
import { Client, PagedClients } from 'lib/Model/Client';
import React, { useContext, useEffect, useState } from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { Activity } from './Activity';
import { Contacts } from './Contacts';
import { Invoices } from './Invoices';
import { MergedCompanies } from './MergedCompanies';
import { RelatedSuggestions } from './RelatedSuggestions';
import {
  resolveMergedAliasesToPlatformNames,
  resolvePlatformName,
  resolvePlatformNameFromPrefix,
  SuggestedMatches,
} from './SuggestedMatches';
import { Wallet } from './Wallet';
import {
  fetchIntraMergesForCompany,
  fetchRelationsForCompany,
} from 'lib/Api/Matches';
import { ProductStack } from './ProductStack';
import { ClientTermsSummary } from './ClientTermsSummary';
import { KeyboardArrowDown, Code, FileCopy } from '@material-ui/icons';
import InfoIcon from '@material-ui/icons/Info';
import { SnackbarContext } from 'components';
import { fetchInvoices } from 'lib/Api/Invoices';
import { toMonetary } from 'lib';
import { subMonths, startOfDay } from 'date-fns';
import CancelIcon from '@material-ui/icons/Cancel';

interface Params {
  id: string;
}

enum ClientDetailsTabs {
  ACTIVITY = 'activity',
  WALLET = 'wallet',
  INVOICES = 'invoices',
  INSIGHTS = 'insights',
  MATCH = 'match',
  CONTACTS = 'contacts',
  DETAILS = 'details',
  PRODUCTSTACK = 'productstack',
  AFFILIATIONS = 'affiliations',
}

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    position: 'relative',
    maxWidth: 420,
    marginBottom: theme.spacing(2),
  },
  searchInput: {
    backgroundColor: theme.palette.background.paper,
  },
  searchAdornmentIcon: {
    color: theme.palette.text.secondary,
  },
  searchResultList: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    borderRadius: theme.shape.borderRadius,
    zIndex: 1300,
  },
  clickableListItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    '& .MuiAccordion-root': {
      boxShadow: 'none',
      border: 'none',
      boxSizing: 'content-box',
      '&:before': {
        display: 'none',
      },
    },
    '& .MuiAccordionDetails-root': {
      padding: 0,
      boxSizing: 'content-box',
    },
  },
  logoContainer: {
    backgroundColor: theme.palette.background.default,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 40,
    color: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    height: '100%',
    minHeight: 200,
    [theme.breakpoints.down('xs')]: {
      minHeight: 120,
      fontSize: 32,
      padding: theme.spacing(2),
    },
  },
  companyInfoContainer: {
    paddingTop: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  companyHeader: {
    marginBottom: theme.spacing(2),
  },
  titleWithFlag: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  headerActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
  actionButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      '& .MuiButtonGroup-root': {
        width: '100%',
      },
      '& .MuiButton-root': {
        width: '100%',
      },
    },
  },
  actionButtonsLeft: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  companyInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  alertStyle: {
    marginBottom: theme.spacing(1),
  },
  lostChip: {
    color: theme.palette.danger.contrastText,
    backgroundColor: theme.palette.danger.light,
    border: 0,
  },
  activeChip: {
    color: theme.palette.success.contrastText,
    backgroundColor: theme.palette.success.light,
    border: 0,
  },
  okChip: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    border: 0,
  },
  smallAvatar: {
    marginRight: theme.spacing(0.5),
    width: 22,
    height: 22,
    fontSize: 12,
  },
  alertAvatar: {
    marginRight: theme.spacing(0.5),
    width: 15,
    height: 15,
    fontSize: 12,
    backgroundColor: theme.palette.error.dark,
    color: theme.palette.error.contrastText,
    marginBottom: theme.spacing(0.5),
  },
  relatedCompaniesNotice: {
    marginBottom: theme.spacing(1),
  },
  techButton: {
    padding: 0,
    minWidth: 'auto',
    marginLeft: theme.spacing(1),
    opacity: 0.6,
    '&:hover': {
      opacity: 1,
      background: 'transparent',
    },
  },
  podContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    // Tight rows, generous column gutter: rhythm without boxing each section
    // into a nested card.
    columnGap: theme.spacing(5),
    rowGap: theme.spacing(3),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  pod: {
    // Flat sections separated by space + a hairline, not nested cards.
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  podTitle: {
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.fontSize * 0.9,
  },
  podContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  podItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '&:hover .copy-button': {
      opacity: 0.7,
      '&:hover': {
        opacity: 1,
      },
    },
  },
  clusterChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  clusterChip: {
    fontSize: theme.typography.fontSize * 0.8,
  },
  disabledChip: {
    opacity: 0.5,
  },
  valueText: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  copyButton: {
    marginLeft: theme.spacing(0.5),
    padding: 4,
    minWidth: 'auto',
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      opacity: 1,
    },
  },
  referrerBadge: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    borderRadius: theme.shape.borderRadius,
    fontWeight: theme.typography.fontWeightMedium,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  struckOffChip: {
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.text.primary,
    '& .MuiChip-icon': {
      color: theme.palette.text.primary,
    },
  },
  struckOffBadge: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius,
    fontWeight: theme.typography.fontWeightMedium,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  orgLvlTxt: {
    fontSize: theme.typography.caption.fontSize,
  },
  parentCompanySection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  parentCompanyInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  orgLevelControl: {
    marginLeft: 'auto',
  },
  headerActionsPositioned: {
    position: 'relative',
    width: '100%',
  },
  techButtonPositioned: {
    position: 'absolute',
    top: '-10px',
    right: 0,
    marginLeft: 0,
  },
  headerFlex: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  contentFlex: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  chipsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridTemplateRows: 'repeat(2, 1fr)',
    gap: theme.spacing(0.5),
    flex: 1,
  },
  infoBtn: {
    padding: 2,
    // matches podTitle's bottom margin so the icon lines up with the text
    marginBottom: theme.spacing(1),
  },
  popoverContent: {
    padding: '8px 16px',
  },
  monospaceText: {
    fontFamily: 'monospace',
  },
  flexWrapGap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  flagIcon: {
    fontSize: '1.2em',
  },
  flagIconBlack: {
    color: theme.palette.text.primary,
  },
  flagIconGrey: {
    color: theme.palette.text.disabled,
  },
  dottedUnderline: {
    borderBottom: '1px dotted',
    cursor: 'pointer',
  },
  flexColumnStart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  flexCenterMargin: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  marginRightSmall: {
    marginRight: '5px',
  },
  marginLeftSmall: {
    marginLeft: '5px',
  },
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  flagIconLarge: {
    fontSize: '2.5em',
  },
  marginLeftMedium: {
    marginLeft: '10px',
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    marginBottom: theme.spacing(2),
    minHeight: 64,
    gap: theme.spacing(2),
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: 1,
    minWidth: 0,
  },
  summaryTitle: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.h5.fontSize,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    flex: '0 1 auto',
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.h6.fontSize,
    },
  },
  summaryMonogram: {
    width: 32,
    height: 32,
    fontSize: theme.typography.caption.fontSize,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  summaryChips: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      '& > :nth-child(n+2)': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  summaryToggle: {
    flexShrink: 0,
    minWidth: 'auto',
    gap: theme.spacing(0.5),
  },
  dialogImage: {
    width: '100%',
    maxWidth: 400,
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  dialogImageContainer: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  overflowChip: {
    fontSize: theme.typography.caption.fontSize,
    height: 24,
    cursor: 'help',
  },
}));

export const ClientDetails: React.FC = () => {
  const match = useRouteMatch<Params>();
  const snackbar = useContext(SnackbarContext);

  const { id } = useParams<Params>();

  const [actionsAnchorEl, setActionsAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const [showIds, setShowIds] = useState(false);
  const [idsAnchorEl, setIdsAnchorEl] = useState<null | HTMLElement>(null);

  const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };
  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const handleIdsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIdsAnchorEl(event.currentTarget);
    setShowIds(!showIds);
  };

  const handleIdsClose = () => {
    setIdsAnchorEl(null);
    setShowIds(false);
  };

  const [hasAffiliations, setHasAffiliations] = useState(false);

  const [showRelatedData, setShowRelatedData] = useState(false);
  const [mergedCompanies, setMergedCompanies] = useState<any[]>([]);

  const [infoOpen, setInfoOpen] = useState(false);

  const classes = useStyles();
  const tabs = [
    { label: 'Activity', value: ClientDetailsTabs.ACTIVITY },
    { label: 'Product Stack', value: ClientDetailsTabs.PRODUCTSTACK },
    { label: 'Wallet', value: ClientDetailsTabs.WALLET },
    { label: 'Invoices', value: ClientDetailsTabs.INVOICES },
    { label: 'Contacts', value: ClientDetailsTabs.CONTACTS },
    ...(hasAffiliations
      ? [
          {
            label: 'Company Hierarchy',
            value: ClientDetailsTabs.AFFILIATIONS,
          },
        ]
      : []),
    { label: 'Profile Management', value: ClientDetailsTabs.MATCH },
  ];

  const history = useHistory();
  const location = useLocation<{ search?: string }>();
  const [clientSearch, setClientSearch] = useState<string>(
    location.state?.search ?? '',
  );
  const debouncedClientSearch = useDebounce(clientSearch, 300);
  const [searchSuggestions, setSearchSuggestions] = useState<
    PagedClients | undefined
  >();
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);

  React.useEffect(() => {
    if (!hasTyped || !debouncedClientSearch) {
      setSearchSuggestions(undefined);
      return;
    }
    setSuggestionsLoading(true);
    fetchClients(1, 5, debouncedClientSearch)
      .then(setSearchSuggestions)
      .finally(() => setSuggestionsLoading(false));
  }, [debouncedClientSearch, hasTyped]);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [data, setData] = React.useState<Client | undefined>();

  const fetchData = React.useCallback(() => {
    setLoading(true);
    setError(false);
    fetchClient(id)
      .then((d) => {
        setLoading(false);
        if (d.id !== id) {
          history.push(`/clients/${d.id}`);
          return;
        }
        setData(d);
      })
      .catch(() => {
        setData(undefined);
        setError(true);
        setLoading(false);
      });
  }, [setLoading, setData, id, history]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data?.id) {
      fetchIntraMergesForCompany(data.id)
        .then((merges) => {
          setMergedCompanies(merges);
        })
        .catch(() => setMergedCompanies([]));
    }
  }, [data?.id]);

  useEffect(() => {
    if (data?.id) {
      fetchRelationsForCompany(data.id, 1, 1)
        .then((d) => {
          setHasAffiliations(Boolean(d?.data && d.data.length > 0));
        })
        .catch(() => setHasAffiliations(false));
    } else {
      setHasAffiliations(false);
    }
  }, [data?.id]);

  const [revenue, setRevenue] = useState<string | undefined>();
  const [revenueLoading, setRevenueLoading] = useState(false);

  React.useEffect(() => {
    if (data?.id) {
      setRevenueLoading(true);
      const today = startOfDay(new Date());
      const dateFrom = subMonths(today, 12).toISOString();
      const dateTo = today.toISOString();
      fetchInvoices(data.id, 1, 1, false, undefined, dateFrom, dateTo)
        .then((response) => {
          setRevenue(toMonetary(response.total_sum));
        })
        .catch(() => {
          setRevenue('N/A');
        })
        .finally(() => {
          setRevenueLoading(false);
        });
    }
  }, [data?.id]);

  useDocumentTitle(data?.company_title ?? undefined);

  const [isCompanyDetailsExpanded, setIsCompanyDetailsExpanded] =
    useState<boolean>(() => {
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem(`clientHeaderCollapsed::${id}`);
        return saved ? JSON.parse(saved) : true;
      }
      return true;
    });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(
        `clientHeaderCollapsed::${id}`,
        JSON.stringify(isCompanyDetailsExpanded),
      );
    }
  }, [isCompanyDetailsExpanded, id]);

  const handleToggleCompanyDetails = () => {
    const newState = !isCompanyDetailsExpanded;
    setIsCompanyDetailsExpanded(newState);

    // Analytics tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'company_details_toggled', {
        company_id: id,
        to_state: newState ? 'expanded' : 'collapsed',
        source: 'chevron',
      });
    }

    if (newState && typeof window !== 'undefined') {
      setTimeout(() => {
        const element = document.getElementById('company-details-region');
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top < 16) {
            window.scrollTo({
              top: window.scrollY + rect.top - 16,
              behavior: 'smooth',
            });
          }
        }
      }, 200);
    }
  };

  if (loading) {
    return (
      <Box>
        <div className={classes.summaryBar}>
          <Skeleton variant="text" width={200} height={32} />
          <Skeleton variant="circle" width={32} height={32} />
          <Skeleton variant="rect" width={80} height={24} />
          <Skeleton variant="rect" width={80} height={24} />
          <Skeleton variant="rect" width={100} height={44} />
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={2}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchData}>
              Retry
            </Button>
          }
        >
          We couldn't load this company. Please try again.
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return <EmptyView />;
  }

  const zendeskPlatform = data.platforms?.find((p) => p.platform === 'zendesk');
  const linkedinLink = zendeskPlatform?.linkedin_link;
  const facebookLink = zendeskPlatform?.facebook_link;

  const zendeskLeader = data.platforms?.find(
    (p) => p.platform === 'zendesk',
  )?.zendesk_leader;

  const zendeskGroupCompanyTitle = data.platforms?.find(
    (p) => p.platform === 'zendesk',
  )?.zendesk_group_company_title;

  const zendeskGroupCompanyId = data.platforms?.find(
    (p) => p.platform === 'zendesk',
  )?.zendesk_group_company_id;

  const blacklisted = data.platforms?.find(
    (p) => p.blacklisted !== null,
  )?.blacklisted;

  const greylisted = data.platforms?.find(
    (p) => p.greylisted !== null,
  )?.greylisted;

  const listingReasons = data.platforms?.find(
    (p) => p.blacklisted_reasons !== null,
  )?.blacklisted_reasons;

  const websites = data.platforms
    ?.filter((p) => p.company_website)
    .map((p) => p.company_website!.replace(/\/$/, '').trim())
    .filter(onlyUnique);

  const industries = data.platforms
    ?.filter((p) => p.industry)
    .map((p) => p.industry!.trim())
    .filter(onlyUnique);

  const headcounts = data.platforms
    ?.filter((p) => p.headcount)
    .map((p) => p.headcount!)
    .filter(onlyUnique);

  const driveFolders = data.platforms
    ?.filter((p) => p.drive_folder_url)
    .map((p) => `https://drive.google.com/drive/folders/${p.drive_folder_url!}`)
    .filter(onlyUnique);

  const cNumbers = data.platforms
    ?.filter((p) => p.c_number)
    .map((p) => p.c_number!)
    .filter(onlyUnique);

  const vatNumbers = data.platforms
    ?.filter((p) => p.vat_number)
    .map((p) => p.vat_number!)
    .filter(onlyUnique);

  const interactionStatus = data.platforms
    ?.filter((p) => p.interaction_status)
    .map((p) => p.interaction_status!)
    .filter(onlyUnique);

  const latestInteraction = data.platforms
    ?.filter((p) => p.latest_interaction)
    .map((p) => p.latest_interaction!)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

  const lifetimeRevenue = revenue || '-';

  const clientPlatforms = [
    ...new Set([
      ...(data.platforms?.map((p) =>
        resolvePlatformNameFromPrefix(p.platform),
      ) || []),
      ...mergedCompanies
        .map((m) =>
          resolvePlatformNameFromPrefix(m.alias_id?.split('_')[0] || ''),
        )
        .filter(Boolean),
    ]),
  ];

  const hasXero = clientPlatforms.some((p) => p.toLowerCase().includes('xero'));

  const hasKonnektOrJim = clientPlatforms.some(
    (p) =>
      p.toLowerCase().includes('konnekt ats') ||
      p.toLowerCase().includes('jobsinmalta'),
  );

  const hasZendesk = clientPlatforms.some((p) =>
    p.toLowerCase().includes('zendesk'),
  );

  const hasZendeskSupport = clientPlatforms.some((p) =>
    p.toLowerCase().includes('zendesk support'),
  );

  const allAvailablePlatforms = [
    'Zendesk Sell',
    'Zendesk Support',
    'Konnekt ATS',
    'Konnekt Payroll',
    'Talexio',
    'jobsinmalta',
    'Xero',
    'Salariesinmalta',
  ];

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        snackbar.success(`${label} copied to clipboard`);
      })
      .catch(() => {
        snackbar.error(`Couldn't copy ${label}. Please copy it manually.`);
      });
  };

  const getSummaryChips = () => {
    const chips = [];

    if (interactionStatus && interactionStatus.length > 0) {
      chips.push({
        label: interactionStatus[0],
        className:
          interactionStatus[0] === 'Engaged'
            ? classes.activeChip
            : interactionStatus[0] === 'Neglected'
            ? classes.lostChip
            : undefined,
      });
    }

    if (zendeskLeader) {
      chips.push({
        label: zendeskLeader,
        avatar: true,
      });
    }

    if (industries && industries.length > 0) {
      chips.push({
        label: industries[0],
      });
    }

    return chips;
  };

  const summaryChips = getSummaryChips();
  const visibleChips = summaryChips.slice(0, 3);
  const hiddenChips = summaryChips.slice(3);

  return (
    <React.Fragment>
      {match && (
        <BreadcrumbsItem to={match.url}>{data.company_title}</BreadcrumbsItem>
      )}

      <ClickAwayListener onClickAway={() => setSearchSuggestions(undefined)}>
        <div className={classes.searchContainer}>
          <TextField
            value={clientSearch}
            variant="outlined"
            size="small"
            onChange={(e) => {
              setHasTyped(true);
              setClientSearch(e.currentTarget.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearchSuggestions(undefined);
                history.push(
                  `/clients${
                    clientSearch
                      ? `?search=${encodeURIComponent(clientSearch)}`
                      : ''
                  }`,
                );
              }
              if (e.key === 'Escape') {
                setSearchSuggestions(undefined);
              }
            }}
            fullWidth
            placeholder="Search for another company..."
            InputProps={{
              className: classes.searchInput,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    className={classes.searchAdornmentIcon}
                  />
                </InputAdornment>
              ),
              endAdornment: suggestionsLoading ? (
                <CircularProgress size={18} />
              ) : null,
            }}
          />
          {searchSuggestions?.data && searchSuggestions.data.length > 0 && (
            <List className={classes.searchResultList}>
              {searchSuggestions.data.map((client) => (
                <ListItem
                  key={client.id}
                  className={classes.clickableListItem}
                  onClick={() => {
                    setSearchSuggestions(undefined);
                    history.push({
                      pathname: `/clients/${client.id}`,
                      state: { search: clientSearch },
                    });
                  }}
                >
                  <ListItemAvatar>
                    <Avatar {...stringAvatar(client.company_title)} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={client.company_title}
                    secondary={
                      <>
                        {resolvePlatformName(client.id)}
                        {resolveMergedAliasesToPlatformNames(
                          client.merged_aliases ?? null,
                        ).length > 0 && (
                          <Tooltip
                            title={
                              <>
                                {resolveMergedAliasesToPlatformNames(
                                  client.merged_aliases ?? null,
                                ).map((name, index) => (
                                  <div key={index}>{name}</div>
                                ))}
                              </>
                            }
                            placement="top"
                          >
                            <Chip
                              label={`${client.merge_count} ${
                                client.merge_count! > 1 ? 'others' : 'other'
                              }`}
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
              {searchSuggestions.total_count >
                searchSuggestions.data.length && (
                <ListItem
                  button
                  onClick={() => {
                    setSearchSuggestions(undefined);
                    history.push(
                      `/clients?search=${encodeURIComponent(clientSearch)}`,
                    );
                  }}
                  style={{ justifyContent: 'center' }}
                >
                  <Button color="primary">
                    View all {searchSuggestions.total_count} results
                  </Button>
                </ListItem>
              )}
            </List>
          )}
        </div>
      </ClickAwayListener>

      <div
        id="company-details-region"
        role="region"
        aria-label="Company details"
      >
        {!isCompanyDetailsExpanded && (
          <div className={classes.summaryBar}>
            <div className={classes.summaryContent}>
              <Tooltip title={data.company_title} placement="bottom-start">
                <Typography variant="h5" className={classes.summaryTitle}>
                  {data.company_title}
                </Typography>
              </Tooltip>

              <Avatar
                className={classes.summaryMonogram}
                {...stringAvatar(data.company_title)}
              />

              <div className={classes.summaryChips}>
                {visibleChips.map((chip, index) => (
                  <Chip
                    key={index}
                    label={chip.label}
                    size="small"
                    className={chip.className}
                    avatar={
                      chip.avatar ? (
                        <Avatar
                          {...stringAvatar(chip.label)}
                          className={classes.smallAvatar}
                        />
                      ) : undefined
                    }
                  />
                ))}

                {hiddenChips.length > 0 && (
                  <Tooltip
                    title={
                      <div>
                        {hiddenChips.map((chip, index) => (
                          <div key={index}>{chip.label}</div>
                        ))}
                      </div>
                    }
                    placement="bottom"
                  >
                    <Chip
                      label={`+${hiddenChips.length}`}
                      size="small"
                      variant="outlined"
                      className={classes.overflowChip}
                    />
                  </Tooltip>
                )}
              </div>

              {data.platforms?.some((p) => p.is_referrer) && (
                <Chip
                  label="Referrer"
                  icon={<PersonAddIcon />}
                  size="small"
                  className={classes.referrerBadge}
                />
              )}

              {data.platforms?.some((p) => p.is_struck_off) && (
                <Chip
                  label="Struck Off"
                  icon={<CancelIcon />}
                  size="small"
                  className={classes.struckOffBadge}
                />
              )}
            </div>

            <Button
              className={classes.summaryToggle}
              onClick={handleToggleCompanyDetails}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggleCompanyDetails();
                }
              }}
              endIcon={<ExpandMoreIcon />}
              aria-controls="company-details-region"
              aria-expanded={isCompanyDetailsExpanded}
              aria-label="Show details, button, collapsed"
            >
              Show details
            </Button>
          </div>
        )}

        <Collapse in={isCompanyDetailsExpanded} timeout={200}>
          <Card className={classes.card}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={4} lg={2}>
                <div
                  className={classes.logoContainer}
                  {...stringAvatar(data.company_title)}
                />
              </Grid>
              <Grid item xs={12} sm={8} md={8} lg={10}>
                <div className={classes.companyInfoContainer}>
                  <div className={classes.companyHeader}>
                    <div
                      className={`${classes.headerActions} ${classes.headerActionsPositioned}`}
                    >
                      <div className={classes.titleWithFlag}>
                        <Typography variant="h4" component="h1">
                          {data.company_title}
                        </Typography>
                        {data.platforms?.some((p) => p.is_referrer) && (
                          <Chip
                            label="Referrer"
                            icon={<PersonAddIcon />}
                            size="small"
                            className={classes.referrerBadge}
                          />
                        )}
                        {data.platforms?.some((p) => p.is_struck_off) && (
                          <Chip
                            label="Struck Off"
                            icon={<CancelIcon />}
                            size="small"
                            className={classes.struckOffBadge}
                          />
                        )}
                      </div>

                      <Button
                        className={`${classes.techButton} ${classes.techButtonPositioned}`}
                        onClick={handleIdsClick}
                        size="small"
                        title="IDs for Nerds"
                        disableRipple
                      >
                        <Code fontSize="small" />
                      </Button>

                      <Popover
                        open={Boolean(idsAnchorEl)}
                        anchorEl={idsAnchorEl}
                        onClose={handleIdsClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <div className={classes.popoverContent}>
                          <Typography variant="subtitle2" gutterBottom>
                            Platform IDs for Nerds
                          </Typography>
                          <div>
                            {getPlatformIds(data).map((item, index) => (
                              <Typography
                                key={index}
                                variant="caption"
                                display="block"
                                className={classes.monospaceText}
                              >
                                {item.platform}: {item.id}
                              </Typography>
                            ))}
                          </div>
                        </div>
                      </Popover>
                    </div>

                    {zendeskGroupCompanyId && (
                      <div className={classes.parentCompanySection}>
                        <div className={classes.parentCompanyInfo}>
                          <Typography variant="subtitle1">
                            Parent Company:
                          </Typography>
                          <RelatedCompanyChip
                            companyId={zendeskGroupCompanyId}
                            companyName={zendeskGroupCompanyTitle}
                          />
                        </div>
                        <FormControl className={classes.orgLevelControl}>
                          <FormControlLabel
                            control={
                              <Switch
                                onChange={(e, checked) =>
                                  setShowRelatedData(checked)
                                }
                                checked={showRelatedData}
                              />
                            }
                            label={
                              <CustomTooltip
                                titleHeader="Organisation Level Data"
                                useDefaultUnderline={true}
                                bodyText="This button will show all data at the organisation level, including all companies and platforms linked to the group."
                              >
                                <span className={classes.orgLvlTxt}>
                                  Show data at organisation level
                                </span>
                              </CustomTooltip>
                            }
                          />
                        </FormControl>
                      </div>
                    )}
                  </div>

                  <div className={classes.podContainer}>
                    <div className={classes.pod}>
                      <Typography className={classes.podTitle}>
                        Basic Information
                      </Typography>
                      <div className={classes.podContent}>
                        {industries && industries.length > 0 && (
                          <div className={classes.podItem}>
                            <Typography variant="caption">Industry:</Typography>
                            <div className={classes.flexWrapGap}>
                              {industries.map((industry, index) => (
                                <Chip
                                  key={`industry_${index}`}
                                  label={industry}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {headcounts && headcounts.length > 0 && (
                          <div className={classes.podItem}>
                            <Typography variant="caption">
                              Headcount:
                            </Typography>
                            <Typography
                              variant="body2"
                              className={classes.valueText}
                            >
                              {headcounts.join(', ')}
                            </Typography>
                          </div>
                        )}

                        {cNumbers && cNumbers.length > 0 && (
                          <div className={classes.podItem}>
                            <Typography variant="caption">
                              Registration No.:
                            </Typography>
                            <Typography
                              variant="body2"
                              className={classes.valueText}
                            >
                              {cNumbers.join(', ')}
                            </Typography>
                            <Button
                              className={`${classes.copyButton} copy-button`}
                              onClick={() =>
                                handleCopyToClipboard(
                                  cNumbers.join(', '),
                                  'Registration number(s)',
                                )
                              }
                              size="small"
                            >
                              <FileCopy fontSize="small" />
                            </Button>
                          </div>
                        )}

                        {vatNumbers && vatNumbers.length > 0 && (
                          <div className={classes.podItem}>
                            <Typography variant="caption">VAT:</Typography>
                            <Typography
                              variant="body2"
                              className={classes.valueText}
                            >
                              {vatNumbers.join(', ')}
                            </Typography>
                            <Button
                              className={`${classes.copyButton} copy-button`}
                              onClick={() =>
                                handleCopyToClipboard(
                                  vatNumbers.join(', '),
                                  'VAT number(s)',
                                )
                              }
                              size="small"
                            >
                              <FileCopy fontSize="small" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={classes.pod}>
                      <Typography className={classes.podTitle}>
                        Account Management
                      </Typography>
                      <div className={classes.podContent}>
                        {zendeskLeader ? (
                          <div className={classes.podItem}>
                            <Typography variant="caption">CAM:</Typography>
                            <Avatar
                              {...stringAvatar(zendeskLeader)}
                              className={classes.smallAvatar}
                            />
                            <Typography
                              variant="body2"
                              className={classes.valueText}
                            >
                              {zendeskLeader}
                            </Typography>
                          </div>
                        ) : (
                          <div className={classes.podItem}>
                            <Avatar className={classes.alertAvatar}>!</Avatar>
                            <Typography variant="body2">
                              CAM not available (Zendesk connection missing)
                            </Typography>
                          </div>
                        )}
                        {interactionStatus && interactionStatus.length > 0 && (
                          <div className={classes.podItem}>
                            <Typography variant="caption">Status:</Typography>
                            <Chip
                              size="small"
                              label={interactionStatus[0]}
                              className={
                                interactionStatus[0] === 'Engaged'
                                  ? classes.activeChip
                                  : interactionStatus[0] === 'Neglected'
                                  ? classes.lostChip
                                  : undefined
                              }
                            />
                          </div>
                        )}
                        {latestInteraction && (
                          <div className={classes.podItem}>
                            <Typography variant="caption">
                              Last Contact:
                            </Typography>
                            <Typography
                              variant="body2"
                              className={classes.valueText}
                            >
                              {new Date(latestInteraction).toLocaleDateString(
                                'en-GB',
                                {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                },
                              )}
                            </Typography>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={classes.pod}>
                      <Typography className={classes.podTitle}>
                        Financial & Status
                      </Typography>
                      <div className={classes.podContent}>
                        <div className={classes.podItem}>
                          <CustomTooltip
                            titleHeader="Revenue Last 12 Months"
                            useDefaultUnderline={true}
                            bodyText="This figure represents the total revenue from invoices issued in the last 12 months."
                            buttonText="View Invoices"
                            onButtonClick={() =>
                              history.push(`${match.url}#invoices`)
                            }
                          >
                            <Typography variant="caption">
                              Revenue Last 12 Months:
                            </Typography>
                          </CustomTooltip>
                          {revenueLoading ? (
                            <Skeleton variant="text" width={80} />
                          ) : (
                            <Typography
                              variant="body2"
                              className={classes.valueText}
                            >
                              {lifetimeRevenue}
                            </Typography>
                          )}
                        </div>

                        <ClientTermsSummary companyId={data.id} />

                        {(blacklisted || greylisted) && (
                          <div className={classes.podItem}>
                            <Flag
                              className={`${classes.flagIcon} ${
                                blacklisted
                                  ? classes.flagIconBlack
                                  : classes.flagIconGrey
                              }`}
                            />
                            <CustomTooltip
                              titleHeader="Listing Reasons"
                              bodyText={listingReasons}
                              placement="top"
                              maxWidth={600}
                              enabledArrow={true}
                            >
                              <Typography
                                variant="caption"
                                className={classes.dottedUnderline}
                              >
                                {blacklisted ? 'Blacklisted' : 'Greylisted'} by:{' '}
                                {(blacklisted || greylisted)
                                  ?.slice(1, -1)
                                  .replace(/'/g, '')
                                  .split(',')
                                  .map((item) => item.trim())
                                  .join(', ')}
                              </Typography>
                            </CustomTooltip>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={classes.pod}>
                      <div className={classes.headerFlex}>
                        <Typography className={classes.podTitle}>
                          Profile Information
                        </Typography>
                        <Tooltip title="Info">
                          <IconButton
                            size="small"
                            className={classes.infoBtn}
                            onClick={() => setInfoOpen(true)}
                            aria-label="Profile information details"
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <div className={classes.podContent}>
                        <div className={classes.contentFlex}>
                          <div className={classes.chipsGrid}>
                            {allAvailablePlatforms.map((platform) => {
                              const isUsed = clientPlatforms.some(
                                (clientPlatform) => {
                                  if (platform === 'Konnekt Payroll') {
                                    return (
                                      clientPlatform
                                        .toLowerCase()
                                        .includes('monday.com') ||
                                      clientPlatform
                                        .toLowerCase()
                                        .includes('konnekt payroll')
                                    );
                                  }
                                  return (
                                    clientPlatform
                                      .toLowerCase()
                                      .includes(platform.toLowerCase()) ||
                                    platform
                                      .toLowerCase()
                                      .includes(clientPlatform.toLowerCase())
                                  );
                                },
                              );
                              return (
                                <Chip
                                  key={platform}
                                  label={platform}
                                  size="small"
                                  variant={isUsed ? 'default' : 'outlined'}
                                  className={`${classes.clusterChip} ${
                                    !isUsed ? classes.disabledChip : ''
                                  }`}
                                  color={isUsed ? 'primary' : 'default'}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Box style={{ marginTop: 16 }}>
                    <div className={classes.actionButtons}>
                      <div className={classes.actionButtonsLeft}>
                        {websites && Boolean(websites.length) && (
                          <ButtonGroup disableElevation variant="contained">
                            {[
                              ...new Set(
                                websites.map((w) => {
                                  try {
                                    return new URL(w.replace('www.', ''))
                                      .hostname;
                                  } catch (error) {
                                    return '';
                                  }
                                }),
                              ),
                            ]
                              .filter(Boolean)
                              .slice(0, 1)
                              .map((domain, index) => (
                                <Button
                                  key={`domain_${index}`}
                                  href={`https://${domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={<LanguageIcon />}
                                >
                                  {domain}
                                </Button>
                              ))}
                          </ButtonGroup>
                        )}

                        <Button
                          aria-controls="actions-menu"
                          aria-haspopup="true"
                          onClick={handleActionsClick}
                          variant="outlined"
                          endIcon={<KeyboardArrowDown />}
                        >
                          Links
                        </Button>
                        <Menu
                          id="actions-menu"
                          anchorEl={actionsAnchorEl}
                          keepMounted
                          open={Boolean(actionsAnchorEl)}
                          onClose={handleActionsClose}
                        >
                          {data.platforms?.map((p, index) => {
                            const platformName = resolvePlatformNameFromPrefix(
                              p.id.split('-')[0].toUpperCase(),
                            );
                            return (
                              <MenuItem
                                key={`platform_link_${index}`}
                                onClick={() => {
                                  window.open(
                                    p.link ?? '',
                                    '_blank',
                                    'noopener,noreferrer',
                                  );
                                  handleActionsClose();
                                }}
                                disabled={!p.link}
                              >
                                {platformName}
                              </MenuItem>
                            );
                          })}
                          <Divider />
                          {driveFolders?.map((w, index) => (
                            <MenuItem
                              key={`folder_${index}`}
                              onClick={() => {
                                window.open(
                                  w ?? '',
                                  '_blank',
                                  'noopener,noreferrer',
                                );
                                handleActionsClose();
                              }}
                            >
                              <CloudIcon
                                fontSize="small"
                                style={{ marginRight: 8 }}
                              />{' '}
                              Drive
                            </MenuItem>
                          ))}
                          {linkedinLink && (
                            <MenuItem
                              onClick={() => {
                                window.open(
                                  linkedinLink,
                                  '_blank',
                                  'noopener,noreferrer',
                                );
                                handleActionsClose();
                              }}
                            >
                              <LinkedInIcon
                                fontSize="small"
                                style={{ marginRight: 8 }}
                              />{' '}
                              LinkedIn
                            </MenuItem>
                          )}
                          {facebookLink && (
                            <MenuItem
                              onClick={() => {
                                window.open(
                                  facebookLink,
                                  '_blank',
                                  'noopener,noreferrer',
                                );
                                handleActionsClose();
                              }}
                            >
                              <FacebookIcon
                                fontSize="small"
                                style={{ marginRight: 8 }}
                              />{' '}
                              Facebook
                            </MenuItem>
                          )}
                          {websites &&
                            [
                              ...new Set(
                                websites.map((w) => {
                                  try {
                                    return new URL(w.replace('www.', ''))
                                      .hostname;
                                  } catch (error) {
                                    return '';
                                  }
                                }),
                              ),
                            ]
                              .filter(Boolean)
                              .slice(1)
                              .map((domain, index) => (
                                <MenuItem
                                  key={`website_menu_${index}`}
                                  onClick={() => {
                                    window.open(
                                      `https://${domain}`,
                                      '_blank',
                                      'noopener,noreferrer',
                                    );
                                    handleActionsClose();
                                  }}
                                >
                                  <LanguageIcon
                                    fontSize="small"
                                    style={{ marginRight: 8 }}
                                  />{' '}
                                  {domain}
                                </MenuItem>
                              ))}
                        </Menu>
                      </div>
                      <Button
                        className={classes.summaryToggle}
                        onClick={handleToggleCompanyDetails}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggleCompanyDetails();
                          }
                        }}
                        endIcon={<ExpandLessIcon />}
                        aria-controls="company-details-region"
                        aria-expanded={isCompanyDetailsExpanded}
                        aria-label="Hide details, button, expanded"
                      >
                        Hide details
                      </Button>
                    </div>
                  </Box>
                </div>
              </Grid>
            </Grid>
          </Card>
        </Collapse>
      </div>

      {showRelatedData && (
        <Alert
          variant="outlined"
          severity="info"
          className={classes.relatedCompaniesNotice}
        >
          <Typography variant="body1">
            Displaying information at an organisation level.
          </Typography>
          <Typography variant="caption">
            Organisation level data is currently unavailable for NPS, Support
            Tickets and Client Wallet.
          </Typography>
        </Alert>
      )}
      <AppTabs tabs={tabs}>
        {(activeTab) => {
          switch (activeTab) {
            case ClientDetailsTabs.ACTIVITY:
              return (
                <Activity
                  companyId={data.id}
                  withRelated={showRelatedData}
                  meetingNotes={
                    data.platforms && data.platforms.length > 0
                      ? data.platforms[0].meeting_notes
                      : undefined
                  }
                  hasKonnektOrJim={hasKonnektOrJim}
                  hasZendesk={hasZendesk}
                  hasZendeskSupport={hasZendeskSupport}
                />
              );
            case ClientDetailsTabs.WALLET:
              return (
                <Wallet companyId={data.id} withRelated={showRelatedData} />
              );
            case ClientDetailsTabs.INVOICES:
              return (
                <Invoices
                  companyId={data.id}
                  withRelated={showRelatedData}
                  hasXero={hasXero}
                />
              );
            case ClientDetailsTabs.PRODUCTSTACK:
              return (
                <ProductStack
                  companyId={data.id}
                  withRelated={showRelatedData}
                />
              );
            case ClientDetailsTabs.CONTACTS:
              return (
                <Contacts companyId={data.id} withRelated={showRelatedData} />
              );
            case ClientDetailsTabs.AFFILIATIONS:
              return (
                <RelatedSuggestions
                  companyId={data.id}
                  doCompanyRefresh={() => {
                    fetchData();
                  }}
                />
              );
            case ClientDetailsTabs.MATCH:
              return (
                <Grid container>
                  <Grid item xs={12} md={6}>
                    <SuggestedMatches
                      companyId={data.id}
                      doCompanyRefresh={() => {
                        fetchData();
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MergedCompanies
                      companyId={data.id}
                      doCompanyRefresh={() => {
                        fetchData();
                      }}
                    />
                  </Grid>
                </Grid>
              );
            default:
              return null;
          }
        }}
      </AppTabs>
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm">
        <DialogTitle>Profile Information</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            This section shows which of our internal platforms contain data for
            this client.
          </Typography>
          <Typography paragraph>
            A <strong>highlighted</strong> platform means a profile from that
            system has been successfully linked (or 'merged') into this central
            view. When linked, you gain access to the following information:
          </Typography>

          <div>
            <Typography>
              - 🤝 <strong>Zendesk Sell:</strong> CAM, Deals, and Call Logs
            </Typography>
            <Typography>
              - 📝 <strong>Konnekt ATS:</strong> Vacancies and Forward
              Applications
            </Typography>
            <Typography>
              - 💼 <strong>Talexio HR:</strong> Client Wallet and Module
              Subscriptions
            </Typography>
            <Typography>
              - 🧾 <strong>Xero:</strong> Complete Invoice History
            </Typography>
            <Typography>
              - 📢 <strong>jobsinmalta:</strong> Active and Past Job Listings
            </Typography>
            <Typography>
              - ✉️ <strong>Zendesk Support:</strong> Client support tickets
            </Typography>
            <Typography>
              - 📊 <strong>Salariesinmalta:</strong> SIM Subscriptions
            </Typography>
          </div>
          <div className={classes.dialogImageContainer}>
            <img
              src="/connections.png"
              alt="Platform overview diagram"
              className={classes.dialogImage}
            />
          </div>
          <Typography paragraph style={{ marginTop: '16px' }}>
            For a more detailed breakdown, including specific module usage and
            subscription expiry dates, please see the{' '}
            <strong>'Product Stack'</strong> section on the client's profile
            page.
          </Typography>
          <Typography paragraph style={{ marginTop: '16px' }}>
            A <strong>greyed-out</strong> platform is a system where we have not
            yet linked a profile for this client. If you believe a profile is
            missing, please contact the CAM or a member of the Commercial or
            Insights Team.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

function onlyUnique(value: string, index: number, array: string[]) {
  return array.indexOf(value) === index;
}
